<template>
  <div :class="['message-wrapper', { 'has-preview': hasPreview }]">
    <div class="content-container">
      <span class="contact-name" :style="{ color: senderColor }">
        {{ isCurrentUser ? $t('CHAT_LIST.YOU') : getSenderName }}
      </span>
      <span v-if="shouldShowSenderPhoneNumber" :style="senderPhoneStyle">
        {{ senderPhoneNumber }}
      </span>

      <div class="message-container">
        <span
          v-if="message.content && !message.attachments"
          class="message-text message-content"
        >
          {{ parsedLastMessage }}
        </span>
        <span v-else-if="isContact" class="message-text">
          <fluent-icon size="16" class="icon-attachment" icon="person" />
          <span class="message-content">
            {{ getContactName }}
          </span>
        </span>
        <span
          v-else-if="message.content && message.attachments"
          class="message-text"
        >
          <fluent-icon
            v-if="attachmentIcon && showMessageType"
            size="16"
            class="icon-attachment"
            :icon="attachmentIcon"
          />
          <span class="message-content">
            {{ parsedLastMessage }}
          </span>
        </span>
        <span v-else-if="message.attachments" class="message-text">
          <fluent-icon
            v-if="attachmentIcon && showMessageType"
            size="16"
            class="icon-attachment"
            :icon="attachmentIcon"
          />
          <!-- Si es audio, mostrar la duración -->
          <span v-if="isAudio" class="message-content">
            {{ audioDuration }}
          </span>
          <!-- Si no es un audio, mostrar el mensaje de contenido de archivo adjunto -->
          <span v-else class="message-content">
            {{ $t(attachmentMessageContent) }}
          </span>
        </span>
        <span v-else class="message-text message-content">
          {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
        </span>
      </div>
    </div>

    <!-- Vista previa de la imagen o video a la derecha -->
    <div v-if="hasPreview" class="preview-container">
      <img
        v-if="isImage"
        :src="
          message.attachments[0].thumb_url || message.attachments[0].data_url
        "
        alt="Preview"
        class="preview-img"
      />
      <video
        v-else-if="isVideo"
        :src="message.attachments[0].data_url"
        playsinline
        class="preview-video"
      />
    </div>
  </div>
</template>

<script>
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';
import { mapGetters } from 'vuex';
import {
  getContactNameShared,
  isAWhatsAppLineChannel,
} from 'dashboard/helper/messageHelper';

export default {
  name: 'MessagePreview',
  mixins: [messageFormatterMixin],
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      audioDuration: null,
      audioObject: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentChat: 'getSelectedChat',
    }),
    // Obtiene el canal actual del chat.
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentChat.inbox_id);
    },
    // La informacion del canal se puede obtener de estas dos formas, depende el mensaje ya existe o si recien se creo el objeto tiene una estructura distinta
    channelType() {
      return this.currentChat?.meta?.channel || this.currentChat?.channel;
    },
    // Comprueba si el mensaje no tiene un remitente o si es un mensaje externo
    fromExternalMessage() {
      return !this.message.sender;
    },
    /** Indica si el chat actual es un grupo. */
    isGroupChat() {
      return typeof this.currentChat.is_group === 'boolean'
        ? this.currentChat.is_group
        : null;
    },
    /** Determina si se debe mostrar el número de teléfono del remitente. */
    shouldShowSenderPhoneNumber() {
      return (
        this.isGroupChat &&
        this.senderPhoneNumber &&
        this.senderPhoneNumber !== this.getSenderName
      );
    },
    /** Obtiene el número de teléfono del remitente. */
    senderPhoneNumber() {
      return this.message?.sender?.phone_number || '';
    },
    /** Define estilos para el número de teléfono del remitente. */
    senderPhoneStyle() {
      return {
        color: this.senderColor,
      };
    },
    // Canal de línea de WhatsApp: sin sender se atribuye al teléfono de la línea (messageHelper).
    isAWhatsAppLineChannel() {
      return isAWhatsAppLineChannel(this.channelType);
    },
    /** Obtiene el nombre del remitente, aplicando lógica para bots y mensajes externos. */
    getSenderName() {
      const customSender = this.message.content_attributes?.custom_sender || {};
      const sender = this.message.sender || {};
      const isSentByBot = sender.type === 'agent_bot';
      let name = sender?.name || customSender.name;

      if (isSentByBot) {
        return `${this.$t('CONVERSATION.BOT')}`;
      }
      if (this.fromExternalMessage) {
        if (this.isAWhatsAppLineChannel) {
          name = this.inbox.phone_number;
        } else {
          name = this.$t('CONVERSATION.FROM_EXTERNAL_MESSAGE');
        }
      }
      return name;
    },
    /** Comprueba si el mensaje fue enviado por el usuario actual. */
    isCurrentUser() {
      return (
        this.message.sender?.id === this.currentUser.id &&
        this.message.sender?.type === 'user'
      );
    },
    // Verifica si el mensaje tiene una vista previa de imagen o video
    hasPreview() {
      const attachments = this.message.attachments || [];
      return attachments.length > 0 && (this.isImage || this.isVideo);
    },

    // Verifica si el archivo adjunto es una imagen
    isImage() {
      return this.lastMessageFileType === 'image';
    },

    // Verifica si el archivo adjunto es un video
    isVideo() {
      return this.lastMessageFileType === 'video';
    },

    // Verifica si el archivo adjunto es un audio
    isAudio() {
      return this.lastMessageFileType === 'audio';
    },

    // Verifica si se compartió un contacto
    isContact() {
      return this.message.content_type === 'contact';
    },

    // Obtiene el nombre del contacto compartido
    getContactName() {
      return getContactNameShared(this.message.content_attributes);
    },

    // Obtiene el tipo de archivo del último mensaje en el chat
    lastMessageFileType() {
      const [{ file_type: fileType } = {}] = this.message.attachments || [];
      return fileType;
    },

    /**
     * Obtiene el contenido parseado del último mensaje en el chat.
     * @returns {string} - El contenido parseado del último mensaje.
     */
    parsedLastMessage() {
      const { content_attributes: contentAttributes } = this.message;
      const { email: { subject } = {} } = contentAttributes || {};
      return this.getPlainText(subject || this.message.content);
    },

    /**
     * Obtiene el icono de adjunto para el último mensaje en el chat.
     * @returns {string} - El icono de adjunto correspondiente al tipo de archivo del último mensaje.
     */
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType] || '';
    },

    /**
     * Obtiene el contenido del mensaje de adjunto.
     * @returns {string} - El contenido del mensaje de adjunto.
     */
    attachmentMessageContent() {
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    /** Retorna el color del remitente o '#000' si no está definido, usando 'unknown_user' para sender nulo. */
    senderColor() {
      const sender = this.message.sender;
      const participantsColors = this.currentChat.participants_colors;

      if (!participantsColors) return '#000';
      const participantKey = sender
        ? `${
            sender.type?.toLowerCase() === 'agent_bot'
              ? 'agentbot'
              : sender.type?.toLowerCase()
          }_${sender.id}`
        : 'unknown_user';
      return participantsColors[participantKey] || '#000';
    },
  },
  watch: {
    'message.attachments': 'loadAudioDuration',
  },
  mounted() {
    this.loadAudioDuration();
  },
  beforeDestroy() {
    if (this.audioObject) {
      this.audioObject.pause();
      this.audioObject = null;
    }
  },
  methods: {
    /**
     * Carga la duración del archivo de audio.
     */
    loadAudioDuration() {
      if (this.isAudio && this.message.attachments.length > 0) {
        const audioUrl = this.message.attachments[0].data_url;

        if (this.audioObject) {
          this.audioObject.pause();
        }

        this.audioObject = new Audio(audioUrl);

        this.audioObject.oncanplaythrough = () => {
          const duration = this.audioObject.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          this.audioDuration = `${minutes}:${
            seconds < 10 ? '0' : ''
          }${seconds}`;
        };

        this.audioObject.onerror = () => {
          this.audioDuration = '00:00';
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.message-wrapper {
  position: relative;
}

.message-wrapper.has-preview {
  padding-right: 60px; /* Deja espacio a la derecha solo si hay un preview */
}

.contact-name {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  line-height: var(--space-normal);
  color: var(--conversa2-blue-700-color);
  cursor: pointer;
}

.not-clickable .contact-name {
  cursor: not-allowed;
}

.message-container {
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  margin-top: 4px;
}

.message-text {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-normal);
  line-height: var(--space-normal);

  display: inline-flex;
  align-items: flex-start;
  flex: 1;
  max-width: 100%;
}

.icon-attachment {
  margin-right: 0.4rem;
  flex-shrink: 0;
  align-self: flex-start;
}

.message-content {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* Limitar a dos líneas */
  overflow: hidden; /* Ocultar el texto que se excede */
}

.preview-container {
  position: absolute;
  top: 50%; /* Centrado verticalmente con respecto al mensaje */
  right: 0; /* Alineado a la derecha de la burbuja */
  transform: translateY(-50%); /* Asegura que esté centrado verticalmente */
  width: 44px;
  height: 36px;
  overflow: hidden;
  border-radius: 2px;
  background-color: #f0f0f0;
  flex-shrink: 0;
}

.preview-img,
.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Asegura que la imagen o video cubran el contenedor */
}
</style>
